import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, AlertTriangle } from 'lucide-react';
import StatusBadge from '../../../components/common/StatusBadge';
import LogicalInterfaceModal from '../../../components/edges/LogicalInterfaceModal';
import InterfaceDependencyWarning, {
  type InterfaceDependencyReport,
} from '../../../components/edges/InterfaceDependencyWarning';
import edgeService from '../../../services/edge.service';
import { useTenantStore } from '../../../stores/tenantStore';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useToast } from '../../../components/common/ToastContainer';
import { usePermissions } from '../../../hooks/usePermissions';

interface ConfigurationWarning {
  type: 'subnet_overlap' | 'missing_wan' | 'missing_lan' | 'dhcp_static_conflict';
  severity: 'warning' | 'error';
  message: string;
  affectedInterfaces: string[];
}

interface LogicalInterfacesConfigProps {
  edgeId: string;
}

const LogicalInterfacesConfig = ({ edgeId }: LogicalInterfacesConfigProps) => {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const { canCreate, canUpdate, canDelete } = usePermissions();
  // Single modal instance for both add and edit — driven by modalState.
  // `editingInterface=null` with `isOpen=true` means "add"; a non-null
  // value means "edit".
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInterface, setEditingInterface] = useState<any>(null);
  const [logicalInterfaces, setLogicalInterfaces] = useState<any[]>([]);
  const [physicalInterfaces, setPhysicalInterfaces] = useState<any[]>([]);
  const [vppStatus, setVppStatus] = useState<any>(null);
  // VSNP-700: see PhysicalInterfacesConfig — separates "still trying to
  // reach the edge" from "tried, came back empty".
  const [vppStatusLoaded, setVppStatusLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    interfaceId: string;
    name: string;
    report: InterfaceDependencyReport | null;
  } | null>(null);
  const [loadingDeps, setLoadingDeps] = useState(false);
  const [configWarnings, setConfigWarnings] = useState<ConfigurationWarning[]>([]);

  useEffect(() => {
    let cancelled = false;

    // VSNP-700: render the table from DB the moment those rows arrive.
    // Live VPP telemetry (DHCP-allocated IP, link state) decorates the
    // already-rendered table when it lands; offline edges no longer
    // block the table on a slow status query.
    const fetchTableData = async () => {
      try {
        setLoading(true);
        const [logical, physical] = await Promise.all([
          edgeService.listLogicalInterfaces(edgeId),
          edgeService.getPhysicalInterfaces(edgeId),
        ]);
        if (cancelled) return;
        setLogicalInterfaces(logical.items || []);
        setConfigWarnings(logical.warnings || []);
        setPhysicalInterfaces(physical.items || []);
      } catch (err) {
        console.error('Failed to load interfaces:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const fetchVppStatus = async () => {
      try {
        const data = await edgeService.getLatestStatus(edgeId);
        if (cancelled) return;
        setVppStatus(data);
      } catch {
        // Non-fatal: rows fall back to their DB-cached fields.
      } finally {
        if (!cancelled) setVppStatusLoaded(true);
      }
    };

    fetchTableData();
    fetchVppStatus();

    return () => {
      cancelled = true;
    };
  }, [edgeId, selectedTenantId]);

  const showOfflineNotice = vppStatusLoaded && !vppStatus;

  // Natural alphanumeric sort by logical interface name so e.g.
  // "LAN-1" < "LAN-2" < "LAN-10" and "WAN-A" < "WAN-B" render in
  // human-friendly order. Same pattern as PhysicalInterfacesConfig.
  const sortedLogicalInterfaces = [...logicalInterfaces].sort((a, b) =>
    (a?.name || '').localeCompare(b?.name || '', undefined, { numeric: true, sensitivity: 'base' }),
  );

  // Get VPP interface by physical name (for DHCP IP lookup)
  const getVPPInterfaceByName = (name: string) => {
    if (!vppStatus?.vpp?.interfaces) return null;
    return vppStatus.vpp.interfaces.find((iface: any) =>
      iface.name === name || iface.name === `GigabitEthernet0/${name.replace('G', '')}/0`
    );
  };

  // Get DHCP-allocated IP from VPP status
  const getDHCPAllocatedIP = (physicalInterface: string): string | null => {
    if (!vppStatus) return null;
    const vppIface = getVPPInterfaceByName(physicalInterface);
    if (!vppIface || !vppIface.ip_addresses || vppIface.ip_addresses.length === 0) return null;
    return vppIface.ip_addresses[0];
  };

  // Pass physical interfaces as-is so the modal can show enabled state.
  const physicalInterfaceList = physicalInterfaces.map((pi) => ({
    name: pi.name,
    id: pi.id,
    configuredState: pi.configuredState,
  }));

  const handleAdd = () => {
    setEditingInterface(null);
    setIsModalOpen(true);
  };

  const handleEdit = (iface: any) => {
    setEditingInterface(iface);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingInterface(null);
  };

  const initiateDelete = async (interfaceId: string, name: string) => {
    setLoadingDeps(true);
    setDeleteConfirm({ interfaceId, name, report: null });

    try {
      const report = await edgeService.getLogicalInterfaceDependencies(edgeId, interfaceId);
      setDeleteConfirm({ interfaceId, name, report });
    } catch (err: any) {
      setDeleteConfirm(null);
      toast.error('Dependency Check Failed', err.message);
    } finally {
      setLoadingDeps(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    const { interfaceId } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await edgeService.deleteLogicalInterface(edgeId, interfaceId);
      // Refresh interfaces and warnings
      const response = await edgeService.listLogicalInterfaces(edgeId);
      setLogicalInterfaces(response.items || []);
      setConfigWarnings(response.warnings || []);
    } catch (err: any) {
      toast.error('Delete Failed', `Failed to delete interface: ${err.message}`);
    }
  };

  /**
   * Persist interface edits (edit mode). New interfaces go through handleCommit
   * (atomic create); the create branch here is a defensive fallback.
   *
   * On error, rethrow so the modal surfaces its own toast and keeps the form
   * open for the user to correct.
   */
  const handleSave = async (data: any): Promise<{ id?: string } | void> => {
    try {
      if (editingInterface) {
        await edgeService.updateLogicalInterface(edgeId, editingInterface.id, data);
        // Refresh + close for updates
        const response = await edgeService.listLogicalInterfaces(edgeId);
        setLogicalInterfaces(response.items || []);
        setConfigWarnings(response.warnings || []);
        return;
      }
      const created = await edgeService.createLogicalInterface(edgeId, data);
      // Refresh the list so the new row appears in the table behind the modal.
      const response = await edgeService.listLogicalInterfaces(edgeId);
      setLogicalInterfaces(response.items || []);
      setConfigWarnings(response.warnings || []);
      // Pass the created id back so the modal can transition add→edit in place
      const createdId = (created && typeof created === 'object')
        ? (created.id || created.data?.id)
        : undefined;
      if (createdId) {
        // The now-persisted interface becomes the "editing" target so
        // subsequent saves hit update() instead of create() again.
        const refreshed = (response.items || []).find((i: any) => i.id === createdId);
        if (refreshed) setEditingInterface(refreshed);
        return { id: createdId };
      }
      return;
    } catch (err: any) {
      // Propagate to the modal, which surfaces the API's real message in a
      // single toast (avoids the duplicate this handler used to add).
      throw err;
    }
  };

  /**
   * Atomic create. The modal drafts the interface and its subscriptions
   * client-side and hands them here for a single transactional commit. On
   * success we refresh the list; on failure we rethrow so the modal surfaces
   * the toast and stays open (nothing was persisted — the whole batch rolled
   * back server-side).
   */
  const handleCommit = async (payload: { interface: any; subscriptions: any[] }): Promise<void> => {
    // Let errors propagate to the modal, which shows a single toast with the
    // API's real message. Toasting here too produced a duplicate.
    await edgeService.commitLogicalInterface(edgeId, payload);
    const response = await edgeService.listLogicalInterfaces(edgeId);
    setLogicalInterfaces(response.items || []);
    setConfigWarnings(response.warnings || []);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Logical Interfaces</h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure virtual interfaces, VLANs, and tunnels
          </p>
        </div>
        {canCreate && (
          <button onClick={handleAdd} className="btn-primary inline-flex items-center space-x-2">
            <Plus className="w-4 h-4" />
            <span>Add Logical Interface</span>
          </button>
        )}
      </div>

      {/* VSNP-700: live telemetry probe finished with nothing — table
          shows DB-cached values; DHCP-allocated IPs and operational
          state will refresh when the edge reconnects. */}
      {showOfflineNotice && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Live telemetry unavailable — values shown are from the last known good state. DHCP-allocated IPs and operational state will update once the edge reconnects.
        </div>
      )}

      {/* Configuration Warnings Banner */}
      {configWarnings.length > 0 && (
        <div className="space-y-2">
          {configWarnings.map((warning, index) => (
            <div
              key={index}
              className={`rounded-lg p-4 flex items-start space-x-3 ${
                warning.severity === 'error'
                  ? 'bg-red-50 border border-red-200'
                  : 'bg-amber-50 border border-amber-200'
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  warning.severity === 'error' ? 'text-red-500' : 'text-amber-500'
                }`}
              />
              <div className="flex-1">
                <h4
                  className={`font-medium text-sm ${
                    warning.severity === 'error' ? 'text-red-800' : 'text-amber-800'
                  }`}
                >
                  {warning.type === 'subnet_overlap'
                    ? 'Subnet Overlap Detected'
                    : 'Configuration Warning'}
                </h4>
                <p
                  className={`text-sm mt-1 ${
                    warning.severity === 'error' ? 'text-red-700' : 'text-amber-700'
                  }`}
                >
                  {warning.message}
                </p>
                {warning.affectedInterfaces.length > 0 && (
                  <p
                    className={`text-xs mt-2 ${
                      warning.severity === 'error' ? 'text-red-600' : 'text-amber-600'
                    }`}
                  >
                    Affected interfaces: {warning.affectedInterfaces.join(', ')}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table — overflow-x-auto (not -hidden) so the wide 9-column layout can
          scroll horizontally on narrow viewports; otherwise the trailing
          Actions column (Edit/Delete) gets clipped off the right edge. */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IPv4/Subnet</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Physical Interface
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">DHCP Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                  Loading interfaces...
                </td>
              </tr>
            ) : logicalInterfaces.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                  No logical interfaces found
                </td>
              </tr>
            ) : (
              sortedLogicalInterfaces.map((iface) => (
              <tr key={iface.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {iface.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded-full ${
                      iface.role === 'WAN' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}
                  >
                    {iface.role}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{iface.type}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                  {iface.dhcpClient || !iface.ipv4 ? (
                    (() => {
                      const dhcpIP = getDHCPAllocatedIP(iface.physicalInterface);
                      return dhcpIP ? (
                        <span className="text-purple-700" title="DHCP-allocated IP from VPP">{dhcpIP}</span>
                      ) : (
                        <span className="text-gray-400 italic">DHCP pending...</span>
                      );
                    })()
                  ) : (
                    iface.cidr ? `${iface.ipv4}/${iface.cidr}` : iface.ipv4
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {/* VSNP-659: render full bridge-member list when present.
                      The API surfaces physicalInterfaceNames[] for multi-port
                      Bridged LAN; falls back to the single physicalInterface. */}
                  {Array.isArray((iface as any).physicalInterfaceNames) && (iface as any).physicalInterfaceNames.length > 1
                    ? (iface as any).physicalInterfaceNames.join(', ')
                    : iface.physicalInterface}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {iface.dhcpClient ? (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700">Enabled</span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">Disabled</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{iface.description}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <StatusBadge status={iface.operationalState} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                  {canUpdate && (
                    <button
                      onClick={() => handleEdit(iface)}
                      className="inline-flex items-center text-primary-600 hover:text-primary-800 font-medium"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => initiateDelete(iface.id, iface.name)}
                      className="inline-flex items-center text-red-600 hover:text-red-800 font-medium"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </button>
                  )}
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal — single instance covers both add and edit.
          `editingInterface` is null on add, the iface row on edit.
          `edgeId` is required for embedded subscription management. */}
      <LogicalInterfaceModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        onCommit={handleCommit}
        interface={editingInterface}
        mode={editingInterface ? 'edit' : 'add'}
        physicalInterfaces={physicalInterfaceList}
        edgeId={edgeId}
        siblingInterfaces={logicalInterfaces.map((li: any) => ({
          id: li.id,
          role: li.role,
          vrfId: li.vrfId,
          name: li.name,
        }))}
      />
      <ConfirmDialog
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete}
        title={`Delete Interface "${deleteConfirm?.name || ''}"`}
        message={
          <InterfaceDependencyWarning
            report={deleteConfirm?.report || null}
            loading={loadingDeps}
            action="delete"
          />
        }
        confirmText="Delete"
        variant="danger"
        isLoading={loadingDeps}
        size="lg"
      />
    </div>
  );
};

export default LogicalInterfacesConfig;

